import {ChangeDetectorRef, Component, ElementRef, Input, OnInit, Output, ViewChild} from '@angular/core';
import {FormControl, FormGroup, Validators} from '@angular/forms';
import {PreLoginSystemConfigurationValues} from '../../configuration/services/prelogin.system.configuration.manager';
import {
  antiPatternValidator, passwordMatchValidator,
  patternValidator
} from '../../../app-auth/login/login-internal/patient.account/password.validators';
import {SystemConfigurationValues} from '../../configuration/services/system.configuration.manager';
import {AuthProviderService} from '../../../app-auth/services/auth-provider.service';

@Component({
  selector: 'app-set-password',
  templateUrl: './set.password.component.html',
  styleUrls: ['./set.password.component.scss']
})
export class SetPasswordComponent implements OnInit {
  @ViewChild('Password', {static: true}) public password: ElementRef;
  @Input() public changePassword = false;

  public formControls: FormGroup;
  public passwordVisible = false;

  public validateNumber = false;
  public validateUpperChar = false;
  public validateLowerChar = false;
  public validateSpecialChar = false;
  public validateLength = false;
  public passwordLength: number;
  public validateConsecutiveChars = false;
  public passwordRecentlyUsed = false;
  public invalidCharacters = false;

  constructor(private changeDetector: ChangeDetectorRef,
              private sysConfigPreLogin: PreLoginSystemConfigurationValues,
              private sysConfig: SystemConfigurationValues,
              private tokenProvider: AuthProviderService) {
  }

  ngOnInit() {
    this.createGroup();
  }

  public isValid(): boolean {
    if (this.formControls.controls.password.invalid) {
      return false;
    }
    this.invalidCharacters = false;
    try {
      const passwordEnc = btoa(this.formControls.controls['password'].value);
    } catch (e) {
      this.invalidCharacters = true;
      return false;
    }
    if (this.formControls.controls.confirmPassword.invalid) {
      return false;
    }
    return true;
  }

  public togglePasswordVisibility() {
    this.passwordVisible = !this.passwordVisible;
  }

  public setPasswordRecentlyUsed() {
    this.passwordRecentlyUsed = true;
    this.changeDetector.markForCheck();
  }

  public resetErrors() {
    this.passwordRecentlyUsed = false;
    this.changeDetector.markForCheck();
  }

  private createGroup(): FormGroup {
    this.initPasswordValidationPattern();

    const validators = [Validators.required];
    // check whether the entered password has a number
    if (this.validateNumber) {
      validators.push(patternValidator(/\d/, {hasNumber: true}));
    }
    // check whether the entered password has upper case letter
    if (this.validateUpperChar) {
      validators.push(patternValidator(/[A-Z]/, {hasCapitalCase: true}));
    }
    // check whether the entered password has a lower-case letter
    if (this.validateLowerChar) {
      validators.push(patternValidator(/[a-z]/, {hasSmallCase: true}));
    }
    // check whether the entered password has a special character
    if (this.validateSpecialChar) {
      validators.push(patternValidator(/[^\w\s]/, {hasSpecialCharacters: true}));
    }
    // has a minimum length of 8 characters
    if (this.validateLength) {
      validators.push(Validators.minLength(this.passwordLength));
    }
    // check whether the entered password has more than 2 consecutive chars
    if (this.validateConsecutiveChars) {
      validators.push(antiPatternValidator(/([^\w\s]|[a-zA-Z0-9])\1\1+/, {lessThanTwoConsecutiveChars: true}));
      // if in the future we want to validate consecutive sequential chars
      // ([^\w\s]|[a-zA-Z0-9])\1\1+|(abc|bcd|cde|def|efg|fgh|ghi|hij|ijk|jkl|klm|lmn|mno|nop|opq|pqr|qrs|rst|stu|tuv|uvw|vwx|wxy|xyz|012|123|234|345|456|567|678|789)+
    }

    return this.formControls = new FormGroup({
        password: new FormControl('', {validators: [...validators]}),
        confirmPassword: new FormControl('', {validators: [Validators.required]})
      },
      {validators: [Validators.required, passwordMatchValidator({passwordsMismatch: true})]}
    );
  }

  private initPasswordValidationPattern() {
    let passwordPattern: number;
    if (this.tokenProvider.isAuthenticated()) {
      passwordPattern = this.sysConfig.UserPasswordPattern;
    } else {
      passwordPattern = this.sysConfigPreLogin.UserPasswordPattern;
    }
    if ((passwordPattern & 1) === 1) {
      this.validateNumber = true;
    }
    if ((passwordPattern & 2) === 2) {
      this.validateUpperChar = true;
    }
    if ((passwordPattern & 4) === 4) {
      this.validateLowerChar = true;
    }
    if ((passwordPattern & 8) === 8) {
      this.validateSpecialChar = true;
    }
    if ((passwordPattern & 16) === 16) {
      this.validateLength = true;
      if (this.tokenProvider.isAuthenticated()) {
        this.passwordLength = this.sysConfig.UserPasswordLength;
      } else {
        this.passwordLength = this.sysConfigPreLogin.UserPasswordLength;
      }
    }
    if ((passwordPattern & 32) === 32) {
      this.validateConsecutiveChars = true;
    }
  }
}
